import { Fragment, useEffect, useLayoutEffect, useRef, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faArrowLeft, faChevronDown, faChevronLeft, faChevronRight, faPaperPlane, faPlus, faTrashCan } from "@fortawesome/pro-regular-svg-icons";
import HeaderBar from "../../components/HeaderBar";
import Sidebar from "../../components/Sidebar";
import ConfirmationModal from "../../components/ConfirmationModal";
import LoadingSpinner from "../../components/LoadingSpinner";
import { FormCard, FormGrid } from "../../components/FormCard";
import { DetailRow } from "../../components/Rows";
import {
  getHubInvoice,
  deleteHubInvoice,
  sendHubInvoice,
  creditHubInvoice,
  checkHubInvoice,
  addHubInvoiceLine,
  updateHubInvoiceLine,
  deleteHubInvoiceLine,
  openHubInvoicePreview,
  refreshHubInvoiceStatus,
} from "../../api/Invoices";
import { getAllChargingSites } from "../../api/ChargingSites";
import { useAuth } from "../../providers/AuthContext";
import { useTranslation } from "react-i18next";
import { formatUiMonthYear } from "../../helpers/DateTime";
import toast from "react-hot-toast";
import { useClearInvoiceFiltersOnLeave } from "./InvoicesPage";

// Wraps long row descriptions and grows with the text so they can be read on narrow screens
const DescriptionField = ({ value, defaultValue, onChange, placeholder }) => {
  const ref = useRef(null);
  const resize = () => {
    if (ref.current) {
      ref.current.style.height = "auto";
      const border = ref.current.offsetHeight - ref.current.clientHeight;
      ref.current.style.height = `${ref.current.scrollHeight + border}px`;
    }
  };
  useLayoutEffect(resize, [value]);
  useEffect(() => {
    window.addEventListener("resize", resize);
    return () => window.removeEventListener("resize", resize);
  }, []);
  return (
    <textarea
      ref={ref}
      rows={1}
      className="input-default-sm block w-full resize-none overflow-hidden !px-3 !py-[5px] leading-5"
      value={value}
      defaultValue={defaultValue}
      placeholder={placeholder}
      onChange={(event) => {
        resize();
        onChange(event);
      }}
    />
  );
};


// Calculated prices like 4.79984679 are shown with at most four decimals. The stored value
// is only replaced when the user edits the field.
const inputNumber = (value) => {
  if (value === "" || value == null) {
    return "";
  }
  const number = Number(value);
  return Number.isFinite(number) ? String(Math.round(number * 10000) / 10000) : value;
};

const invoiceStatusClass = (status) => {
  const classes = {
    CreatedInDb: "bg-primary-100 text-primary-700",
    NoCharge: "bg-mute-100 text-mute-700",
    Failed: "bg-error-100 text-error-700",
    FortnoxCheckRequired: "bg-kraft-100 text-kraft-800",
    Sending: "bg-secondary-100 text-secondary-800",
    SentToFortnox: "bg-success-100 text-success-800",
    Cancelling: "bg-kraft-100 text-kraft-800",
    Credited: "bg-volt-100 text-volt-700",
    Cancelled: "bg-mute-200 text-mute-800",
  };
  return classes[status] || "bg-mute-100 text-mute-700";
};

const invoiceTypeClass = (type) => {
  const classes = {
    CustomerInvoice: "bg-primary-100 text-primary-700",
    PartnerCustomerInvoice: "bg-secondary-100 text-secondary-800",
    PartnerSupplierInvoice: "bg-volt-100 text-volt-700",
  };
  return classes[type] || "bg-mute-100 text-mute-700";
};

const InvoiceDetailPage = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  useClearInvoiceFiltersOnLeave();
  const { t, i18n } = useTranslation();
  const { getAccessToken } = useAuth();
  const [invoice, setInvoice] = useState(null);
  const [loadFailed, setLoadFailed] = useState(false);
  const [waitFrom, setWaitFrom] = useState(null);
  const [confirmDelete, setConfirmDelete] = useState(false);
  const [pendingLine, setPendingLine] = useState(null);
  const [confirmAction, setConfirmAction] = useState(null);
  const [linePage, setLinePage] = useState(1);
  const [openGroups, setOpenGroups] = useState({});
  const [saving, setSaving] = useState(false);
  const [line, setLine] = useState({ description: "", quantity: "", unitPrice: "", unit: "st", chargingSitePublicId: "", discountType: "", discountValue: "" });
  const [sites, setSites] = useState([]);
  const customerInvoiceType = invoice?.invoiceType === "CustomerInvoice";

  // Manual rows on customer invoices need a site, picked from the list instead of typing its id
  useEffect(() => {
    if (!customerInvoiceType || sites.length > 0) {
      return;
    }
    const loadSites = async () => {
      const accessToken = await getAccessToken();
      const rows = await getAllChargingSites(accessToken);
      const list = (rows ?? []).filter((site) => site.chargingSitePublicId);
      list.sort((left, right) => String(left.name || left.chargingSiteName || "").localeCompare(String(right.name || right.chargingSiteName || ""), "sv"));
      setSites(list);
    };
    loadSites().catch((error) => console.error(error));
  // eslint-disable-next-line react-hooks/exhaustive-deps -- load once per customer invoice
  }, [customerInvoiceType]);
  const [drafts, setDrafts] = useState({});
  const setDraft = (lineId, patch) => {
    setDrafts((current) => {
      const next = { ...current, [lineId]: { ...current[lineId], ...patch } };
      const groupIds = new Set((invoice?.lines ?? []).filter((row) => row.lineSource === "Group").map((row) => row.invoiceLineId));
      if (!groupIds.has(lineId)) {
        groupIds.forEach((id) => {
          delete next[id];
        });
      }
      return next;
    });
  };
  const failureText = (error, fallback = "invoice.saveFailed") => {
    const reason = error?.response?.data?.reason;
    return typeof reason === "string" && reason.trim() ? reason : t(fallback);
  };
  const deleteLine = async (row) => {
    const toastId = toast.loading(t("invoice.deletingLine"));
    try {
      const accessToken = await getAccessToken();
      await deleteHubInvoiceLine(invoice.invoiceId, row.invoiceLineId, accessToken);
      toast.success(t(row.lineSource === "Session" ? "invoice.sessionZeroed" : "invoice.lineDeleted"), { id: toastId });
      await load(false);
    } catch (error) {
      toast.error(failureText(error, "invoice.deleteLineFailed"), { id: toastId });
      console.error(error);
    }
  };

  const load = async (requestStatus = true) => {
    try {
      const accessToken = await getAccessToken();
      setInvoice(await getHubInvoice(id, accessToken, requestStatus));
      setLoadFailed(false);
    } catch (error) {
      if (requestStatus) {
        setLoadFailed(true);
      }
      console.error(error);
    }
  };

  useEffect(() => {
    setWaitFrom(null);
    setLoadFailed(false);
    setLinePage(1);
    load(true);
  // eslint-disable-next-line react-hooks/exhaustive-deps -- intentional effect scope
  }, [id]);

  useEffect(() => {
    const statusWaiting = invoice?.fortnoxStatusFetchStatus === "Pending";
    const supplierWaiting = invoice?.supplierFetchStatus === "Pending";
    if (!invoice || (!statusWaiting && !supplierWaiting)) {
      setWaitFrom(null);
      return undefined;
    }
    setWaitFrom((current) => current ?? Date.now());
    const started = waitFrom ?? Date.now();
    if (statusWaiting && !supplierWaiting && Date.now() - started > 30000) {
      return undefined;
    }
    const timer = setInterval(() => {
      load(false);
    }, 3000);
    return () => clearInterval(timer);
  }, [invoice, waitFrom]);

  const run = async (action, pending, ok, fail) => {
    const toastId = toast.loading(t(pending));
    try {
      const accessToken = await getAccessToken();
      await action(accessToken);
      if (ok) {
        toast.success(t(ok), { id: toastId });
      } else {
        toast.dismiss(toastId);
      }
      setWaitFrom(null);
      await load(true);
    } catch (error) {
      toast.error(t(fail), { id: toastId });
      console.error(error);
    }
  };

  if (!invoice) {
    return (
      <>
        <HeaderBar />
        <div className="flex flex-row w-full">
          <Sidebar />
          <main className="page flex items-center justify-center">
            {loadFailed ? (
              <div className="page-content w-full">
                <button type="button" className="text-black flex items-center pb-4" onClick={() => navigate("/invoices")}>
                  <FontAwesomeIcon icon={faArrowLeft} className="w-4 pr-4" />
                  <span className="border-l border-mute-200 pl-4">{t("invoice.back")}</span>
                </button>
                <p className="text-sm">{t("invoice.loadFailed")}</p>
              </div>
            ) : (
              <LoadingSpinner text={t("common.loadingEntity", { name: t("invoice.title") })} />
            )}
          </main>
        </div>
      </>
    );
  }

  const waiting = invoice.fortnoxStatusFetchStatus === "Pending";
  const timedOut = waiting && waitFrom != null && Date.now() - waitFrom > 30000;
  const fetched = invoice.fortnoxStatusFetchStatus === "Fetched" && !waiting;
  const reasons = (invoice.cannotSend ?? []).map((reason) => {
    const text = t(`invoice.reason.${reason.code}`, { detail: reason.detail ?? "" });
    if (reason.code === "supplierFetchFailed" && reason.detail) {
      return `${text}: ${reason.detail}`;
    }
    return text;
  });

  const period = `${invoice.periodStart ? String(invoice.periodStart).slice(0, 10) : ""} – ${invoice.periodEnd ? String(invoice.periodEnd).slice(0, 10) : ""}`;
  const periodMonth = invoice.periodStart ? `${String(invoice.periodStart).slice(0, 10)}T00:00:00` : null;
  const heading = [invoice.partyName, formatUiMonthYear(periodMonth, i18n.resolvedLanguage || i18n.language)].filter((part) => part).join(", ");
  const statusText = `${t(`invoice.statuses.${invoice.status}`, { defaultValue: invoice.status })}${invoice.stuck ? ` (${t("invoice.stuck")})` : ""}`;
  const formatAmount = (value) => {
    const number = Number(value);
    if (!Number.isFinite(number)) {
      return "";
    }
    return number.toLocaleString("sv-SE", { minimumFractionDigits: 2, maximumFractionDigits: 2 });
  };
  const lineTotal = (row) => {
    const draft = drafts[row.invoiceLineId] || {};
    const quantity = Number(draft.quantity ?? row.quantity);
    const unitPrice = Number(draft.unitPrice ?? row.amountExclVat);
    if (!Number.isFinite(quantity) || !Number.isFinite(unitPrice)) {
      return row.amount;
    }
    const gross = unitPrice * quantity;
    const type = draft.discountType != null ? draft.discountType : (row.discountType || "");
    const raw = draft.discountValue != null && draft.discountValue !== "" ? draft.discountValue : row.discountValue;
    const discount = Number(raw);
    if (type === "PERCENT" && Number.isFinite(discount)) {
      return gross - (gross * discount / 100);
    }
    if (type === "AMOUNT" && Number.isFinite(discount)) {
      const hasGroups = (invoice.lines ?? []).some((item) => item.lineSource === "Group");
      const perKwh = row.lineSource === "Session" && invoice.invoiceType === "CustomerInvoice" && hasGroups;
      return gross - (perKwh ? discount * quantity : discount);
    }
    return gross;
  };
  const sourceLabel = (source) => t(`invoice.lineSources.${source}`, { defaultValue: source || "" });
  const unitChoices = (current) => {
    const choices = [
      { value: "kWh", label: t("invoice.units.kWh") },
      { value: "st", label: t("invoice.units.st") },
    ];
    if (invoice?.invoiceType !== "CustomerInvoice") {
      choices.push(
        { value: "anläggning", label: t("invoice.units.site") },
        { value: "uttag", label: t("invoice.units.connector") },
      );
    }
    if (current && !choices.some((choice) => choice.value === current)) {
      choices.push({ value: current, label: unitLabel(current) });
    }
    return choices;
  };
  const unitLabel = (unit) => {
    const keys = {
      "Profit share": "invoice.units.profitShare",
      Session: "invoice.units.session",
    };
    return keys[unit] ? t(keys[unit]) : unit;
  };
  const lines = invoice.lines ?? [];
  const partnerInvoice = invoice.invoiceType === "PartnerCustomerInvoice" || invoice.invoiceType === "PartnerSupplierInvoice";
  const groups = lines.filter((row) => row.lineSource === "Group");
  const sessions = lines.filter((row) => row.lineSource === "Session" && !partnerInvoice);
  const priceKey = (row) => {
    const price = Number(row.amountExclVat);
    const rate = Number(row.discountValue);
    return [
      row.chargingSiteId ?? "",
      Number.isFinite(price) ? price.toFixed(4) : "",
      row.discountType ?? "",
      Number.isFinite(rate) ? rate.toFixed(4) : "",
    ].join("|");
  };
  const consumedSessions = new Set();
  const childrenByGroup = new Map();
  groups.forEach((group) => {
    childrenByGroup.set(group.invoiceLineId, []);
  });
  groups.forEach((group) => {
    const sameSite = groups.filter((candidate) => candidate.chargingSiteId != null && candidate.chargingSiteId === group.chargingSiteId);
    if (sameSite.length <= 1 || group.chargingSiteId == null) {
      return;
    }
    sessions.forEach((session) => {
      if (consumedSessions.has(session.invoiceLineId) || session.chargingSiteId !== group.chargingSiteId) {
        return;
      }
      if (priceKey(session) !== priceKey(group)) {
        return;
      }
      consumedSessions.add(session.invoiceLineId);
      childrenByGroup.get(group.invoiceLineId).push(session);
    });
  });
  groups.forEach((group) => {
    if (group.chargingSiteId == null) {
      return;
    }
    const sameSite = groups.filter((candidate) => candidate.chargingSiteId === group.chargingSiteId);
    const kwhGroups = sameSite.filter((candidate) => String(candidate.unit || "").toLowerCase() === "kwh");
    const pool = kwhGroups.length > 0 ? kwhGroups : sameSite;
    const winner = pool.reduce((best, candidate) => (Number(candidate.quantity) > Number(best.quantity) ? candidate : best));
    if (winner.invoiceLineId !== group.invoiceLineId) {
      return;
    }
    sessions.forEach((session) => {
      if (consumedSessions.has(session.invoiceLineId) || session.chargingSiteId !== group.chargingSiteId) {
        return;
      }
      consumedSessions.add(session.invoiceLineId);
      childrenByGroup.get(group.invoiceLineId).push(session);
    });
  });
  const groupFigures = (group, children) => {
    if (children.length === 0) {
      return {
        quantity: group.quantity,
        unitPrice: group.amountExclVat,
        amount: lineTotal(group),
      };
    }
    const quantity = children.reduce((sum, child) => {
      const draft = drafts[child.invoiceLineId] || {};
      const value = Number(draft.quantity ?? child.quantity);
      return sum + (Number.isFinite(value) ? value : 0);
    }, 0);
    const amount = children.reduce((sum, child) => sum + (Number(lineTotal(child)) || 0), 0);
    return {
      quantity,
      unitPrice: quantity !== 0 ? amount / quantity : 0,
      amount,
    };
  };
  const textLines = invoice.invoiceType === "CustomerInvoice"
    ? lines.filter((row) => row.lineSource === "Text")
    : [];
  const lineBlocks = [
    ...groups.map((row) => ({ kind: "group", row, children: childrenByGroup.get(row.invoiceLineId) || [] })),
    ...sessions.filter((row) => !consumedSessions.has(row.invoiceLineId)).map((row) => ({ kind: "line", row })),
    ...lines.filter((row) => row.lineSource !== "Group" && row.lineSource !== "Session" && row.lineSource !== "Text").map((row) => ({ kind: "line", row })),
  ];
  const linePageSize = 50;
  const linePageCount = Math.max(1, Math.ceil(lineBlocks.length / linePageSize));
  const currentLinePage = Math.min(linePage, linePageCount);
  const visibleBlocks = lineBlocks.slice((currentLinePage - 1) * linePageSize, currentLinePage * linePageSize);
  const groupBaseline = (row, children) => {
    const childDirty = children.some((child) => drafts[child.invoiceLineId]);
    if (childDirty) {
      return groupFigures(row, children);
    }
    return {
      quantity: row.quantity,
      unitPrice: row.amountExclVat,
      amount: lineTotal(row),
    };
  };
  const groupAmount = (row, children) => {
    const draft = drafts[row.invoiceLineId] || {};
    const childDirty = children.some((child) => drafts[child.invoiceLineId]);
    const figures = groupBaseline(row, children);
    if (childDirty && draft.quantity == null && draft.unitPrice == null && draft.discountType == null && draft.discountValue == null) {
      return figures.amount;
    }
    const quantity = Number(draft.quantity ?? row.quantity);
    const unitPrice = Number(draft.unitPrice ?? row.amountExclVat);
    if (!Number.isFinite(quantity) || !Number.isFinite(unitPrice)) {
      return figures.amount;
    }
    const gross = quantity * unitPrice;
    const type = draft.discountType != null ? draft.discountType : (row.discountType || "");
    const raw = draft.discountValue != null && draft.discountValue !== "" ? draft.discountValue : row.discountValue;
    const discount = Number(raw);
    if (type === "PERCENT" && Number.isFinite(discount)) {
      return gross - (gross * discount / 100);
    }
    if (type === "AMOUNT" && Number.isFinite(discount)) {
      return gross - discount;
    }
    return gross;
  };
  const discountChanged = (row) => {
    const draft = drafts[row.invoiceLineId];
    if (!draft) {
      return false;
    }
    const typeChanged = draft.discountType != null && draft.discountType !== (row.discountType || "");
    const valueChanged = draft.discountValue != null && String(draft.discountValue) !== String(row.discountValue ?? "");
    return typeChanged || valueChanged;
  };
  const liveAmount = lineBlocks.reduce((sum, block) => {
    if (block.kind === "group") {
      return sum + (Number(groupAmount(block.row, block.children)) || 0);
    }
    if (block.row.lineSource === "Text") {
      return sum;
    }
    return sum + (Number(lineTotal(block.row)) || 0);
  }, 0);
  const dirtyGroups = groups.filter((row) => {
    const draft = drafts[row.invoiceLineId];
    if (!draft) {
      return false;
    }
    const figures = groupBaseline(row, childrenByGroup.get(row.invoiceLineId) || []);
    const quantityChanged = draft.quantity != null && String(draft.quantity) !== String(figures.quantity);
    const priceChanged = draft.unitPrice != null && String(draft.unitPrice) !== String(figures.unitPrice);
    const descriptionChanged = draft.description != null && draft.description !== (row.description ?? "");
    const unitChanged = draft.unit != null && draft.unit !== (row.unit ?? "");
    return quantityChanged || priceChanged || descriptionChanged || unitChanged || discountChanged(row);
  });
  const dirtySessions = sessions.filter((row) => {
    const draft = drafts[row.invoiceLineId];
    if (!draft) {
      return false;
    }
    const quantityChanged = draft.quantity != null && String(draft.quantity) !== String(row.quantity);
    const priceChanged = draft.unitPrice != null && String(draft.unitPrice) !== String(row.amountExclVat ?? "");
    return quantityChanged || priceChanged || discountChanged(row);
  });
  const dirtyPlain = lines.filter((row) => {
    if (row.lineSource !== "Fee" && row.lineSource !== "Manual") {
      return false;
    }
    const draft = drafts[row.invoiceLineId];
    if (!draft) {
      return false;
    }
    const quantityChanged = draft.quantity != null && String(draft.quantity) !== String(row.quantity);
    const priceChanged = draft.unitPrice != null && String(draft.unitPrice) !== String(row.amountExclVat ?? "");
    const descriptionChanged = draft.description != null && draft.description !== (row.description ?? "");
    const unitChanged = draft.unit != null && draft.unit !== (row.unit ?? "");
    return quantityChanged || priceChanged || descriptionChanged || unitChanged;
  });
  const saveLines = async () => {
    if ((dirtySessions.length === 0 && dirtyGroups.length === 0 && dirtyPlain.length === 0) || saving) {
      return;
    }
    setSaving(true);
    const toastId = toast.loading(t("invoice.saving"));
    try {
      const accessToken = await getAccessToken();
      const changed = [
        ...dirtySessions.map((row) => ({ row, fallbackPrice: row.amountExclVat })),
        ...dirtyGroups.map((row) => {
          // Customer groups are rebuilt from their sessions, so only the description is saved on them
          if (invoice.invoiceType === "CustomerInvoice") {
            return { row, fallbackPrice: row.amountExclVat, fallbackQuantity: row.quantity };
          }
          const figures = groupBaseline(row, childrenByGroup.get(row.invoiceLineId) || []);
          return { row, fallbackPrice: figures.unitPrice, fallbackQuantity: figures.quantity };
        }),
        ...dirtyPlain.map((row) => ({ row, fallbackPrice: row.amountExclVat, fallbackQuantity: row.quantity })),
      ];
      for (let index = 0; index < changed.length; index += 1) {
        const item = changed[index];
        const draft = drafts[item.row.invoiceLineId] || {};
        const payload = {
          quantity: Number(draft.quantity ?? item.fallbackQuantity ?? item.row.quantity),
          unitPrice: Number(draft.unitPrice ?? item.fallbackPrice),
        };
        if (item.row.lineSource === "Group" || item.row.lineSource === "Fee" || item.row.lineSource === "Manual") {
          payload.description = draft.description ?? item.row.description ?? "";
          payload.unit = draft.unit ?? item.row.unit ?? "";
        }
        if (invoice.invoiceType === "CustomerInvoice" && item.row.lineSource === "Session") {
          const draftType = draft.discountType != null ? draft.discountType : (item.row.discountType || "");
          const draftValue = draft.discountValue != null ? draft.discountValue : item.row.discountValue;
          payload.updateDiscount = true;
          payload.discountType = draftType || null;
          payload.discountValue = draftValue === "" || draftValue == null ? null : Number(draftValue);
        }
        await updateHubInvoiceLine(invoice.invoiceId, item.row.invoiceLineId, payload, accessToken);
      }
      setInvoice((current) => {
        if (!current?.lines) {
          return current;
        }
        return {
          ...current,
          lines: current.lines.map((row) => {
            const draft = drafts[row.invoiceLineId];
            if (!draft) {
              return row;
            }
            const quantity = draft.quantity != null && draft.quantity !== "" ? Number(draft.quantity) : row.quantity;
            const unitPrice = draft.unitPrice != null && draft.unitPrice !== "" ? Number(draft.unitPrice) : row.amountExclVat;
            return {
              ...row,
              quantity: Number.isFinite(quantity) ? quantity : row.quantity,
              amountExclVat: Number.isFinite(unitPrice) ? unitPrice : row.amountExclVat,
              description: draft.description ?? row.description,
              unit: draft.unit ?? row.unit,
              discountType: draft.discountType != null ? (draft.discountType || null) : row.discountType,
              discountValue: draft.discountValue != null && draft.discountValue !== ""
                ? Number(draft.discountValue)
                : (draft.discountValue === "" ? null : row.discountValue),
            };
          }),
        };
      });
      setDrafts({});
      toast.success(t("invoice.saved"), { id: toastId });
      await load(false);
    } catch (error) {
      toast.error(failureText(error), { id: toastId });
      console.error(error);
    } finally {
      setSaving(false);
    }
  };
  const customerInvoice = invoice.invoiceType === "CustomerInvoice";
  const renderDiscount = (row, perKwh) => {
    if (!customerInvoice) {
      return null;
    }
    if (row.lineSource !== "Group" && row.lineSource !== "Session") {
      return <td className="align-middle" />;
    }
    const draft = drafts[row.invoiceLineId] || {};
    const typeValue = draft.discountType != null ? draft.discountType : (row.discountType || "");
    const numberValue = draft.discountValue != null ? draft.discountValue : (row.discountValue ?? "");
    // A group shows the discount of its sessions; the discount is set on each session
    if (!invoice.canEdit || row.lineSource === "Group") {
      if (!typeValue || numberValue === "" || numberValue == null) {
        return <td className="align-middle" />;
      }
      const suffix = typeValue === "PERCENT" ? t("invoice.discountPercent") : (perKwh ? t("invoice.discountPerKwh") : t("invoice.discountAmount"));
      return <td className="align-middle">{`${numberValue} ${suffix}`}</td>;
    }
    const choices = [
      { value: "", label: "–", title: t("invoice.discountNone") },
      { value: "PERCENT", label: t("invoice.discountPercent"), title: t("invoice.discountPercent") },
      { value: "AMOUNT", label: perKwh ? t("invoice.discountPerKwh") : t("invoice.discountAmount"), title: perKwh ? t("invoice.discountPerKwh") : t("invoice.discountAmount") },
    ];
    return (
      <td className="align-middle">
        <div className="flex items-center gap-1">
          <div className="inline-flex overflow-hidden rounded-full border border-primary-200">
            {choices.map((choice) => {
              const active = typeValue === choice.value;
              return (
                <button
                  key={choice.value || "none"}
                  type="button"
                  title={choice.title}
                  className={`h-6 px-1.5 text-xs font-semibold leading-6 ${active ? "bg-primary-300 text-primary-700" : "bg-white text-mute-700 hover:bg-primary-50"}`}
                  onClick={() => setDraft(row.invoiceLineId, choice.value === "" ? { discountType: "", discountValue: "" } : { discountType: choice.value })}
                >
                  {choice.label}
                </button>
              );
            })}
          </div>
          {typeValue && (
            <input
              className="!h-6 !w-12 !rounded-md !border !border-primary-200 !bg-white !px-1.5 !py-0 !text-xs !leading-6"
              value={numberValue}
              onChange={(event) => setDraft(row.invoiceLineId, { discountValue: event.target.value })}
            />
          )}
        </div>
      </td>
    );
  };
  const renderLine = (row, nested) => {
    const plainEdit = invoice.canEdit && (row.lineSource === "Fee" || row.lineSource === "Manual");
    const draft = drafts[row.invoiceLineId] || {};
    const unitValue = draft.unit ?? row.unit ?? "";
    return (
    <tr key={row.invoiceLineId}>
      <td className={`align-middle ${nested ? "pl-10" : ""}`}>
        {plainEdit ? (
          <DescriptionField defaultValue={row.description ?? ""} onChange={(event) => setDraft(row.invoiceLineId, { description: event.target.value })} />
        ) : row.description}
      </td>
      <td className="align-middle text-right">
        {invoice.canEdit && (row.lineSource === "Session" || plainEdit) ? (
          <input className="input-default-sm w-full !px-2 text-right" defaultValue={inputNumber(row.quantity)} onChange={(event) => setDraft(row.invoiceLineId, { quantity: event.target.value })} />
        ) : row.quantity}
      </td>
      <td className="align-middle text-right">
        {invoice.canEdit && (row.lineSource === "Session" || plainEdit) ? (
          <input className="input-default-sm w-full !px-2 text-right" defaultValue={inputNumber(row.amountExclVat)} onChange={(event) => setDraft(row.invoiceLineId, { unitPrice: event.target.value })} />
        ) : row.amountExclVat}
      </td>
      {renderDiscount(row, row.lineSource === "Session" && groups.length > 0)}
      <td className="align-middle text-right">{formatAmount(lineTotal(row))}</td>
      <td className="align-middle !pl-6">
        {plainEdit ? (
          <select className="h-8 w-full rounded-md border border-mute-200 bg-white pl-2 pr-7 text-sm leading-8" value={unitValue} onChange={(event) => setDraft(row.invoiceLineId, { unit: event.target.value })}>
            {unitChoices(unitValue).map((choice) => (
              <option key={choice.value} value={choice.value}>{choice.label}</option>
            ))}
          </select>
        ) : unitLabel(row.unit)}
      </td>
      <td className="align-middle">{sourceLabel(row.lineSource)}</td>
      <td className="align-middle">
        <div className="flex justify-end items-center gap-3">
          {invoice.canEdit && (row.lineSource === "Manual" || row.lineSource === "Fee") && (
            <button
              type="button"
              className="flex h-7 w-7 items-center justify-center rounded-full text-error-500 transition-colors hover:bg-error-100 hover:text-error-600"
              title={t("invoice.delete")}
              onClick={() => setPendingLine(row)}
            >
              <FontAwesomeIcon icon={faTrashCan} className="text-base" />
            </button>
          )}
        </div>
      </td>
    </tr>
    );
  };
  const draftQuantity = Number(line.quantity);
  const draftPrice = Number(line.unitPrice);
  const draftDiscount = Number(line.discountValue);
  let draftTotal = null;
  if (Number.isFinite(draftQuantity) && Number.isFinite(draftPrice)) {
    const gross = draftQuantity * draftPrice;
    if (line.discountType === "PERCENT" && Number.isFinite(draftDiscount)) {
      draftTotal = gross - (gross * draftDiscount / 100);
    } else if (line.discountType === "AMOUNT" && Number.isFinite(draftDiscount)) {
      draftTotal = gross - draftDiscount;
    } else {
      draftTotal = gross;
    }
  }
  const addLine = async () => {
    const toastId = toast.loading(t("invoice.addingLine"));
    try {
      const accessToken = await getAccessToken();
      const payload = {
        description: line.description,
        quantity: Number(line.quantity),
        unitPrice: Number(line.unitPrice),
        unit: line.unit,
        chargingSitePublicId: line.chargingSitePublicId || null,
      };
      if (invoice.invoiceType === "CustomerInvoice" && line.discountType) {
        payload.discountType = line.discountType;
        payload.discountValue = line.discountValue === "" ? null : Number(line.discountValue);
      }
      await addHubInvoiceLine(invoice.invoiceId, payload, accessToken);
      setLine({ description: "", quantity: "", unitPrice: "", unit: "st", chargingSitePublicId: "", discountType: "", discountValue: "" });
      toast.success(t("invoice.lineAdded"), { id: toastId });
      await load(false);
    } catch (error) {
      toast.error(failureText(error, "invoice.addLineFailed"), { id: toastId });
      console.error(error);
    }
  };

  return (
    <>
      <HeaderBar />
      <div className="flex flex-row w-full">
        <Sidebar />
        <main className="page">
          <div className="page-content">
            <div className="flex flex-row items-center pb-4">
              <button type="button" className="text-black flex" onClick={() => navigate("/invoices")}>
                <FontAwesomeIcon icon={faArrowLeft} className="w-4 pr-4" />
              </button>
              <h2 className="border-l border-mute-200 pl-4">{heading || invoice.invoicePublicId}</h2>
              {invoice.thirdPartyInvoiceReferenceId && (
                <span className="border-l border-mute-200 ml-4 pl-4 text-base">{t("invoice.fortnoxNumber")} {invoice.thirdPartyInvoiceReferenceId}</span>
              )}
              <div className="flex flex-wrap gap-4 ml-auto">
                {invoice.canEdit && (
                  <button type="button" className="btn btn-primary" disabled={(dirtySessions.length === 0 && dirtyGroups.length === 0 && dirtyPlain.length === 0) || saving} onClick={saveLines}>
                    {t("common.save")}
                  </button>
                )}
                {invoice.canSend && <button type="button" className="btn btn-primary" onClick={() => setConfirmAction("send")}>{t("invoice.send")}</button>}
                {fetched && invoice.canCredit && <button type="button" className="btn btn-primary" onClick={() => setConfirmAction("credit")}>{t("invoice.credit")}</button>}
                {fetched && invoice.canCancel && <button type="button" className="btn btn-primary" onClick={() => setConfirmAction("cancel")}>{t("invoice.cancel")}</button>}
                {invoice.canCheck && <button type="button" className="btn btn-outline-primary" onClick={() => run((token) => checkHubInvoice(invoice.invoiceId, token), "invoice.checking", "invoice.checkQueued", "invoice.checkFailed")}>{t("invoice.check")}</button>}
                {(timedOut || invoice.fortnoxStatusFetchStatus === "Failed") && <button type="button" className="btn btn-outline-primary" onClick={() => run((token) => refreshHubInvoiceStatus(invoice.invoiceId, token), "invoice.fetchingStatus", "invoice.statusQueued", "invoice.statusTimeout")}>{t("invoice.tryAgain")}</button>}
                {invoice.status !== "Credited" && invoice.status !== "Cancelled" && (invoice.thirdPartyInvoiceReferenceId || invoice.status === "SentToFortnox" || invoice.status === "Cancelling") ? <button type="button" className="btn btn-outline-primary" onClick={() => run((token) => refreshHubInvoiceStatus(invoice.invoiceId, token), "invoice.fetchingStatus", "invoice.statusQueued", "invoice.statusTimeout")}>{t("invoice.updateStatus")}</button> : null}
                {invoice.canDelete && <button type="button" className="btn btn-outline-primary" onClick={() => setConfirmDelete(true)}>{t("invoice.delete")}</button>}
                {invoice.invoiceType !== "PartnerCustomerInvoice" && (
                  <button type="button" className="btn btn-outline-primary" onClick={() => run((token) => openHubInvoicePreview(invoice.invoiceId, token, invoice.createdDate), "invoice.previewOpening", null, "invoice.previewFailed")}>{t("invoice.preview")}</button>
                )}
              </div>
            </div>
            <div className="flex flex-col gap-4">
              <FormCard>
                <FormGrid>
                  <DetailRow label={invoice.invoiceType === "CustomerInvoice" ? t("entity.customer") : t("entity.partner")} value={invoice.partyName} />
                  <DetailRow
                    label={t("invoice.type")}
                    value={(
                      <span className={`inline-flex items-center rounded-full px-2.5 py-0.5 text-xs font-semibold ${invoiceTypeClass(invoice.invoiceType)}`}>
                        {t(`invoice.types.${invoice.invoiceType}`, { defaultValue: invoice.invoiceType })}
                      </span>
                    )}
                  />
                  <DetailRow
                    label={t("invoice.status")}
                    value={(
                      <span className={`inline-flex items-center rounded-full px-2.5 py-0.5 text-xs font-semibold ${invoiceStatusClass(invoice.status)}`}>
                        {statusText}
                      </span>
                    )}
                  />
                  <DetailRow label={t("invoice.period")} value={period} />
                  <DetailRow label={t("invoice.amount")} value={formatAmount(liveAmount)} />
                  {invoice.thirdPartyInvoiceReferenceId ? <DetailRow label={t("invoice.fortnoxNumber")} value={invoice.thirdPartyInvoiceReferenceId} /> : null}
                  {invoice.creditDocumentNumber ? <DetailRow label={t("invoice.creditInvoice")} value={invoice.creditDocumentNumber} /> : null}
                  {reasons.length > 0 ? <DetailRow label={t("invoice.cannotSend")} value={reasons.join(", ")} /> : null}
                  {invoice.emailError ? <DetailRow label={t("invoice.emailUnknown")} value={invoice.emailError} /> : null}
                </FormGrid>
                {invoice.lastError && <p className="text-sm pt-4">{invoice.lastError}</p>}
                {waiting && !timedOut && <p className="text-sm pt-4">{t("invoice.fetchingStatus")}</p>}
                {timedOut && <p className="text-sm pt-4">{t("invoice.statusTimeout")}</p>}
                {invoice.emailStatus === "Pending" && <p className="text-sm pt-4">{t("invoice.emailUnknown")}</p>}
              </FormCard>
              <FormCard title={t("invoice.lines")}>
                <div className="overflow-x-auto">
                  <table className="table-default table-dense w-full">
                    <thead>
                      <tr>
                        <th className="min-w-64">{t("invoice.description")}</th>
                        <th className="w-24 !text-right">{t("invoice.quantity")}</th>
                        <th className="w-32 !text-right">
                          <span className="block">{t("invoice.unitPrice")}</span>
                          <span className="block text-xs font-normal">{t("common.exclVat")}</span>
                        </th>
                        {invoice.invoiceType === "CustomerInvoice" && <th className="w-36">{t("invoice.discount")}</th>}
                        <th className="w-32 !text-right">
                          <span className="block">{t("common.amount")}</span>
                          <span className="block text-xs font-normal">{t("common.exclVat")}</span>
                        </th>
                        <th className="w-44 !pl-6">{t("invoice.unit")}</th>
                        <th className="w-28">{t("invoice.lineSource")}</th>
                        <th className="w-12" />
                      </tr>
                    </thead>
                    <tbody>
                      {visibleBlocks.map((block) => {
                        if (block.kind !== "group") {
                          return renderLine(block.row, false);
                        }
                        const open = !!openGroups[block.row.invoiceLineId];
                        const figures = groupBaseline(block.row, block.children);
                        const draft = drafts[block.row.invoiceLineId] || {};
                        const quantityValue = draft.quantity ?? inputNumber(figures.quantity);
                        const priceValue = draft.unitPrice ?? inputNumber(figures.unitPrice);
                        const descriptionValue = draft.description ?? block.row.description ?? "";
                        return (
                          <Fragment key={block.row.invoiceLineId}>
                            <tr>
                              <td className="align-middle">
                                <div className="flex items-center gap-2">
                                  {block.children.length > 0 && (
                                    <button
                                      type="button"
                                      className="flex items-center text-left"
                                      onClick={() => setOpenGroups((current) => ({ ...current, [block.row.invoiceLineId]: !current[block.row.invoiceLineId] }))}
                                    >
                                      <FontAwesomeIcon icon={open ? faChevronDown : faChevronRight} className="w-5 h-5" />
                                    </button>
                                  )}
                                  {invoice.canEdit ? (
                                    <DescriptionField value={descriptionValue} onChange={(event) => setDraft(block.row.invoiceLineId, { description: event.target.value })} />
                                  ) : (
                                    <span>{block.row.description}</span>
                                  )}
                                </div>
                              </td>
                              <td className="align-middle text-right">
                                {invoice.canEdit && !customerInvoice ? (
                                  <input className="input-default-sm w-full !px-2 text-right" value={quantityValue} onChange={(event) => setDraft(block.row.invoiceLineId, { quantity: event.target.value })} />
                                ) : formatAmount(figures.quantity)}
                              </td>
                              <td className="align-middle text-right">
                                {invoice.canEdit && !customerInvoice ? (
                                  <input className="input-default-sm w-full !px-2 text-right" value={priceValue} onChange={(event) => setDraft(block.row.invoiceLineId, { unitPrice: event.target.value })} />
                                ) : inputNumber(figures.unitPrice).replace(".", ",")}
                              </td>
                              {renderDiscount(block.row, false)}
                              <td className="align-middle text-right">{formatAmount(groupAmount(block.row, block.children))}</td>
                              <td className="align-middle !pl-6">
                                {unitLabel(block.row.unit)}
                              </td>
                              <td className="align-middle">{sourceLabel(block.row.lineSource)}</td>
                              <td className="align-middle">
                                <div className="flex justify-end items-center gap-3">
                                  {invoice.canEdit && partnerInvoice && (
                                    <button
                                      type="button"
                                      className="flex h-7 w-7 items-center justify-center rounded-full text-error-500 transition-colors hover:bg-error-100 hover:text-error-600"
                                      title={t("invoice.delete")}
                                      onClick={() => setPendingLine(block.row)}
                                    >
                                      <FontAwesomeIcon icon={faTrashCan} className="text-base" />
                                    </button>
                                  )}
                                </div>
                              </td>
                            </tr>
                            {open && block.children.map((child) => renderLine(child, true))}
                          </Fragment>
                        );
                      })}
                      {textLines.map((row) => (
                        <tr key={row.invoiceLineId}>
                          <td className="align-middle" colSpan={invoice.invoiceType === "CustomerInvoice" ? 6 : 5}>{row.description}</td>
                          <td className="align-middle">{sourceLabel(row.lineSource)}</td>
                          <td />
                        </tr>
                      ))}
                      {invoice.canEdit && (
                        <tr>
                          <td className="align-middle">
                            <DescriptionField
                              placeholder={t("invoice.description")}
                              value={line.description}
                              onChange={(event) => setLine({ ...line, description: event.target.value })}
                            />
                          </td>
                          <td className="align-middle text-right">
                            <input
                              className="input-default-sm w-full !px-2 text-right"
                              placeholder={t("invoice.quantity")}
                              value={line.quantity}
                              onChange={(event) => setLine({ ...line, quantity: event.target.value })}
                            />
                          </td>
                          <td className="align-middle text-right">
                            <input
                              className="input-default-sm w-full !px-2 text-right"
                              placeholder={t("invoice.unitPrice")}
                              value={line.unitPrice}
                              onChange={(event) => setLine({ ...line, unitPrice: event.target.value })}
                            />
                          </td>
                          {invoice.invoiceType === "CustomerInvoice" && (
                            <td className="align-middle">
                              <div className="flex items-center gap-1">
                                <div className="inline-flex overflow-hidden rounded-full border border-primary-200">
                                  {[
                                    { value: "", label: "–", title: t("invoice.discountNone") },
                                    { value: "PERCENT", label: t("invoice.discountPercent"), title: t("invoice.discountPercent") },
                                    { value: "AMOUNT", label: t("invoice.discountAmount"), title: t("invoice.discountAmount") },
                                  ].map((choice) => {
                                    const active = line.discountType === choice.value;
                                    return (
                                      <button
                                        key={choice.value || "none"}
                                        type="button"
                                        title={choice.title}
                                        className={`h-6 px-1.5 text-xs font-semibold leading-6 ${active ? "bg-primary-300 text-primary-700" : "bg-white text-mute-700 hover:bg-primary-50"}`}
                                        onClick={() => setLine({ ...line, discountType: choice.value, discountValue: choice.value === "" ? "" : line.discountValue })}
                                      >
                                        {choice.label}
                                      </button>
                                    );
                                  })}
                                </div>
                                {line.discountType && (
                                  <input
                                    className="!h-6 !w-12 !rounded-md !border !border-primary-200 !bg-white !px-1.5 !py-0 !text-xs !leading-6"
                                    value={line.discountValue}
                                    onChange={(event) => setLine({ ...line, discountValue: event.target.value })}
                                  />
                                )}
                              </div>
                            </td>
                          )}
                          <td className="align-middle text-right">{draftTotal == null ? "" : formatAmount(draftTotal)}</td>
                          <td className="align-middle !pl-6">
                            <select className="h-8 w-full rounded-md border border-mute-200 bg-white pl-2 pr-7 text-sm leading-8" value={line.unit} onChange={(event) => setLine({ ...line, unit: event.target.value })}>
                              {unitChoices(line.unit).map((choice) => (
                                <option key={choice.value} value={choice.value}>{choice.label}</option>
                              ))}
                            </select>
                          </td>
                          <td className="align-middle">
                            {invoice.invoiceType === "CustomerInvoice" && (
                              <select
                                className="h-8 w-full rounded-md border border-mute-200 bg-white pl-2 pr-7 text-sm leading-8"
                                value={line.chargingSitePublicId}
                                onChange={(event) => setLine({ ...line, chargingSitePublicId: event.target.value })}
                              >
                                <option value="">{t("entity.site")}</option>
                                {sites.map((site) => (
                                  <option key={site.chargingSitePublicId} value={site.chargingSitePublicId}>{site.name || site.chargingSiteName || site.chargingSitePublicId}</option>
                                ))}
                              </select>
                            )}
                          </td>
                          <td className="align-middle">
                            <div className="flex justify-end">
                              <button
                                type="button"
                                className="text-primary-600 hover:text-primary-800"
                                title={t("invoice.addLine")}
                                onClick={addLine}
                              >
                                <FontAwesomeIcon icon={faPlus} className="w-5 h-5" />
                              </button>
                            </div>
                          </td>
                        </tr>
                      )}
                    </tbody>
                  </table>
                </div>
                {lineBlocks.length > linePageSize && (
                  <div className="flex gap-4 justify-end items-center pt-4">
                    <p>{t("pagination.range", {
                      start: (currentLinePage - 1) * linePageSize + 1,
                      end: Math.min(currentLinePage * linePageSize, lineBlocks.length),
                      total: lineBlocks.length,
                    })}</p>
                    <FontAwesomeIcon
                      className={`cursor-pointer ${currentLinePage <= 1 ? "opacity-40 pointer-events-none" : ""}`}
                      icon={faChevronLeft}
                      onClick={() => {
                        if (currentLinePage > 1) {
                          setLinePage(currentLinePage - 1);
                        }
                      }}
                    />
                    <FontAwesomeIcon
                      className={`cursor-pointer ${currentLinePage >= linePageCount ? "opacity-40 pointer-events-none" : ""}`}
                      icon={faChevronRight}
                      onClick={() => {
                        if (currentLinePage < linePageCount) {
                          setLinePage(currentLinePage + 1);
                        }
                      }}
                    />
                  </div>
                )}
              </FormCard>
            </div>
          </div>
          <ConfirmationModal
            isOpen={confirmAction != null}
            {...(confirmAction === "send" ? { icon: faPaperPlane, tone: "primary" } : {})}
            onClose={() => setConfirmAction(null)}
            title={t(confirmAction === "credit" ? "invoice.credit" : confirmAction === "cancel" ? "invoice.cancel" : "invoice.send")}
            description={confirmAction === "credit"
              ? t("invoice.creditConfirm")
              : confirmAction === "cancel"
                ? t("invoice.cancelConfirm")
                : invoice.invoiceType === "PartnerSupplierInvoice"
                  ? t("invoice.sendConfirmSupplier", { party: invoice.partyName || "", email: invoice.supplierEmail || "" })
                  : t("invoice.sendConfirm")}
            okText={t(confirmAction === "credit" ? "invoice.credit" : confirmAction === "cancel" ? "invoice.cancel" : "invoice.send")}
            onConfirm={() => {
              const action = confirmAction;
              setConfirmAction(null);
              if (action === "send") {
                run((token) => sendHubInvoice(invoice.invoiceId, token), "invoice.sending", "invoice.sent", "invoice.sendFailed");
                return;
              }
              if (action === "cancel") {
                run((token) => creditHubInvoice(invoice.invoiceId, token), "invoice.cancelling", "invoice.cancelQueued", "invoice.cancelFailed");
                return;
              }
              run((token) => creditHubInvoice(invoice.invoiceId, token), "invoice.crediting", "invoice.creditQueued", "invoice.creditFailed");
            }}
          />
          <ConfirmationModal
            isOpen={pendingLine != null}
            onClose={() => setPendingLine(null)}
            title={t("invoice.delete")}
            description={pendingLine?.lineSource === "Session" ? t("invoice.deleteSessionConfirm") : t("invoice.deleteLineConfirm")}
            okText={t("invoice.delete")}
            onConfirm={() => {
              const row = pendingLine;
              setPendingLine(null);
              if (row) {
                deleteLine(row);
              }
            }}
          />
          <ConfirmationModal
            isOpen={confirmDelete}
            onClose={() => setConfirmDelete(false)}
            title={t("invoice.delete")}
            description={t("invoice.deleteConfirm")}
            okText={t("invoice.delete")}
            onConfirm={async () => {
              setConfirmDelete(false);
              const toastId = toast.loading(t("invoice.deleting"));
              try {
                const accessToken = await getAccessToken();
                await deleteHubInvoice(invoice.invoiceId, accessToken);
                toast.success(t("invoice.deleted"), { id: toastId });
                navigate("/invoices");
              } catch (error) {
                toast.error(t("invoice.deleteFailed"), { id: toastId });
                console.error(error);
              }
            }}
          />
        </main>
      </div>
    </>
  );
};

export default InvoiceDetailPage;
